/**
 * Autosave for the device inspection page.
 *
 * The wizard this replaced saved on every Next, which is what made progress
 * survive a phone dying mid-inspection. A single page has no Next, so the
 * answers save themselves shortly after each change instead.
 *
 * - **One save at a time, in order.** Saves run on a promise chain and each
 *   sends the *latest* draft when it starts, so a slow request can never land
 *   after a newer one and roll the answers back.
 * - **The first save starts the inspection** (`not_started` → `in_progress`).
 *   After that the status is never sent: a completed inspection stays complete
 *   when it is edited (owner's decision, 2026-10-10), and only the explicit
 *   "Mark inspection complete" sends `complete`.
 * - **A failed save retries** on a timer, and the page warns before unload
 *   while anything is unsaved.
 * - sm4rt-api writes no audit row for an answers-only save, so this does not
 *   flood the session's Audit panel.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CheckInDeviceType, CheckInInspectionStatus } from '@techgitter/contracts';

import { patchCheckInDraftJob } from '../services/checkInApi';
import { draftToInspection, type InspectionDraftState } from '../utils/inspectionSchema';

const SAVE_DELAY_MS = 700;
const RETRY_DELAY_MS = 5000;

export type AutosaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface UseInspectionAutosaveArgs {
  sessionId: string;
  draftJobId: string;
  deviceType: CheckInDeviceType;
  draft: InspectionDraftState;
  inspectionStatus: CheckInInspectionStatus;
  /** False for a read-only inspection: nothing is ever sent. */
  enabled: boolean;
  /** Called after the save that moved the inspection to in_progress. */
  onStarted?: () => void;
}

export interface UseInspectionAutosaveResult {
  state: AutosaveState;
  error: string | null;
  /** Save anything pending now; resolves once it has landed (rejects on failure). */
  flush: () => Promise<void>;
}

export function useInspectionAutosave({
  sessionId,
  draftJobId,
  deviceType,
  draft,
  inspectionStatus,
  enabled,
  onStarted,
}: UseInspectionAutosaveArgs): UseInspectionAutosaveResult {
  const serialized = useMemo(
    () => JSON.stringify(draftToInspection(deviceType, draft)),
    [deviceType, draft],
  );

  // What the server holds. Seeded with what was loaded, so opening the page
  // saves nothing.
  const lastSaved = useRef(serialized);
  const latest = useRef(serialized);
  latest.current = serialized;
  const status = useRef(inspectionStatus);
  useEffect(() => {
    status.current = inspectionStatus;
  }, [inspectionStatus]);
  const onStartedRef = useRef(onStarted);
  onStartedRef.current = onStarted;

  const chain = useRef<Promise<void>>(Promise.resolve());
  const timer = useRef<number | null>(null);
  const mounted = useRef(true);
  const [state, setState] = useState<AutosaveState>('idle');
  const [error, setError] = useState<string | null>(null);

  const saveLatest = useCallback(async () => {
    const body = latest.current;
    if (body === lastSaved.current) return;
    const starting = status.current === 'not_started';
    if (mounted.current) setState('saving');
    try {
      await patchCheckInDraftJob(sessionId, draftJobId, {
        inspection: JSON.parse(body),
        ...(starting ? { inspectionStatus: 'in_progress' as const } : {}),
      });
      lastSaved.current = body;
      if (starting) {
        status.current = 'in_progress';
        onStartedRef.current?.();
      }
      if (mounted.current) {
        setError(null);
        setState(latest.current === body ? 'saved' : 'saving');
      }
    } catch (err) {
      if (mounted.current) {
        setError(err instanceof Error ? err.message : 'Failed to save');
        setState('error');
      }
      throw err;
    }
  }, [sessionId, draftJobId]);

  const clearTimer = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };

  const enqueue = useCallback((): Promise<void> => {
    const run = chain.current.catch(() => undefined).then(saveLatest);
    chain.current = run;
    run.catch(() => {
      // Retry while the page is open; a newer change re-arms the timer anyway.
      if (!mounted.current) return;
      clearTimer();
      timer.current = window.setTimeout(() => {
        timer.current = null;
        void enqueue().catch(() => undefined);
      }, RETRY_DELAY_MS);
    });
    return run;
  }, [saveLatest]);

  useEffect(() => {
    if (!enabled || serialized === lastSaved.current) return;
    clearTimer();
    timer.current = window.setTimeout(() => {
      timer.current = null;
      void enqueue().catch(() => undefined);
    }, SAVE_DELAY_MS);
  }, [enabled, serialized, enqueue]);

  // Leaving with something unsaved: save it on the way out, and let the
  // browser warn if the tab itself is closing.
  useEffect(() => {
    if (!enabled) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (latest.current !== lastSaved.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [enabled]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimer();
      if (latest.current !== lastSaved.current) void enqueue().catch(() => undefined);
    };
  }, [enqueue]);

  const flush = useCallback(() => {
    clearTimer();
    return enqueue();
  }, [enqueue]);

  return { state, error, flush };
}
